import React, { useContext, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Link as ChakraLink,
  useColorModeValue,
  Image,
  Button,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import AuthContext from "../context/AuthContext"; // adjust path to your AuthContext
import finanseeLogo from "../assets/finanseeLogo.png"; // adjust path if needed
import { API_BASE_URL } from "../config/adminConfig";

function Navbar() {
  const { token, user, logout, setUser } = useContext(AuthContext);
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");
  const border = useColorModeValue("gray.200", "gray.700");
  const activeColor = useColorModeValue("#007AFF", "blue.300"); // FinanSee blue

  // Check user on component mount and when token changes
  useEffect(() => {
    const checkUser = async () => {
      if (token) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/auth/check-user`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const data = await response.json();
          if (response.status === 200) {
            setUser(data.user); // ✅ Save user in context
          } else {
            if (data.message.includes("Please login again")) {
              logout();
            } else {
              toast({
                title: "Error",
                description: "An unexpected error occurred.",
                status: "error",
                duration: 5000,
                position: "top",
                isClosable: true,
              });
            }
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to check token validity.",
            status: "error",
            duration: 5000,
            position: "top",
            isClosable: true,
          });
          logout();
        }
      }
    };

    if (token) {
      checkUser();
    }
    // eslint-disable-next-line
  }, [token]);

  return (
    <Box
      bg={bg}
      borderBottom="1px solid"
      borderColor={border}
      px={6}
      py={4}
      shadow="sm"
      position="sticky"
      top="0"
      zIndex="1000"
    >
      <Flex maxW="100%" align="center" justify="space-between" gap={6} wrap="wrap">
        {/* Logo + Title */}
        <Flex align="center" gap={8}>
          <Image
            src={finanseeLogo}
            alt="FinanSee Logo"
            boxSize="40px"
            objectFit="contain"
          />
          <Text fontSize="xl" fontWeight="700" color="#000">
            FinanSee Admin Console
          </Text>
        </Flex>

        {/* Navigation Links + User Info */}
        <Flex align="center" gap={{ base: 3, md: 5 }} wrap="wrap">
          <ChakraLink as={NavLink} to="/contacts" fontWeight="700">Website contacts</ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/"
            fontWeight="500"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            User Data
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/b2b"
            fontWeight="700"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            B2B Metrics
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/onboardingFunnel"
            fontWeight="500"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            Onboarding Funnel
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/productAnalytics"
            fontWeight="700"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            Growth &amp; Revenue
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/engagement"
            fontWeight="700"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            Engagement
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/aiAnalytics"
            fontWeight="500"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            AI Governance
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/blogsVideo"
            fontWeight="500"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            Paid Content
          </ChakraLink>
          <ChakraLink
            as={NavLink}
            to="/feedEngagement"
            fontWeight="500"
            _hover={{ color: activeColor }}
            _activeLink={{
              color: activeColor,
              borderBottom: `2px solid ${activeColor}`,
            }}
            pb="1px"
          >
            Feed Engagement
          </ChakraLink>
          {/* User Name + Logout Button */}
          {user && (
            <Flex align="center" gap={4}>
              <Text fontWeight="500" color="gray.500" textDecoration="underline">
                Hi! {user.name}
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={logout}
              >
                Logout
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

export default Navbar;
